import React, { Suspense } from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { IntlProvider } from "react-intl";
import { ErrorBoundary } from "react-error-boundary";

import logger from "utils/logger.js";
import routes from "./config/routes";
import enMessages from "./config/locales/en";

/**
 * App — root component.
 *
 * Phase 1/2/4 client revamp: replaces the old base-shell + material-ui-shell
 * scaffolding with a plain React Router + react-intl setup. The shells used
 * to wrap every page in a Container with a constrained max-width and to add
 * a drawer / app bar that we don't want for the guest ordering flow. Each
 * page now renders its own chrome (see SignIn / Home).
 *
 * Routes live in `./config/routes`; they're just an array of <Route/> and
 * <PrivateRoute/> JSX so we can spread them straight into the Switch here.
 */
function ErrorFallback() {
    return null;
}

function onError(error, info) {
    logger.push({ error, info });
}

const PageNotFound = React.lazy(() =>
    import("./pages/PageNotFound/PageNotFound")
);

export default function App() {
    return (
        <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
            <IntlProvider locale="en" messages={enMessages} defaultLocale="en">
                <BrowserRouter>
                    <Suspense fallback={<RouteSuspense />}>
                        <Switch>
                            {routes.map((route, idx) => React.cloneElement(route, { key: idx }))}
                            <Route component={PageNotFound} />
                        </Switch>
                    </Suspense>
                </BrowserRouter>
            </IntlProvider>
        </ErrorBoundary>
    );
}

function RouteSuspense() {
    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-bg-base">
            <div className="inline-flex items-center gap-2 text-fg-muted text-sm">
                <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span>Loading…</span>
            </div>
        </div>
    );
}
